import React from "react"

import { normalizeLocale } from "../../config"
import { createTranslator } from "../../i18n/translator"
import { ClearIcon } from "../icon"
import {
  getAccountHasError,
  getAccountValidationError,
  resolveAccountType,
  type AccountInputMode,
  type AccountInputValue,
  type PhoneCodeOption,
} from "./account-input-model"
import { isEventWithinElement } from "./event-path"
import {
  getPhoneCodeOptionsStorage,
  initializePhoneCodeOptions,
  loadPhoneCodeOptions,
  readPhoneCodeOptionsCache,
  type PhoneCodeProtocol,
} from "./phone-code-loader"

import "../Input.scss"
import "./AccountInput.scss"

export {
  composeCellphone,
  getAccountHasError,
  getAccountValidationError,
  isSamePhone,
  normalizePhoneNumber,
  resolveAccountType,
} from "./account-input-model"
export type { AccountInputMode, AccountInputValue, ResolvedAccountType } from "./account-input-model"

export type AccountInputProps = {
  mode: AccountInputMode
  locale: string
  value: AccountInputValue
  onChange: (value: AccountInputValue, hasError: boolean) => void
  disabled?: boolean
  error?: string
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onEnter?: () => void
  className?: string
  id?: string
  name?: string
  placeholder?: string
}

type PhoneCodeState = {
  options: PhoneCodeOption[]
  loading: boolean
  failed: boolean
}

const EMPTY_PHONE_CODE_STATE: PhoneCodeState = { options: [], loading: false, failed: false }
const canLoadPhoneCodes = (mode: AccountInputMode): boolean => mode !== "email"

const getInitialPhoneCodeState = (mode: AccountInputMode): PhoneCodeState => {
  if (!canLoadPhoneCodes(mode)) return EMPTY_PHONE_CODE_STATE
  const options = readPhoneCodeOptionsCache(
    getPhoneCodeOptionsStorage(typeof window === "undefined" ? undefined : window)
  )
  return options ? { options, loading: false, failed: false } : EMPTY_PHONE_CODE_STATE
}

const requestPhoneCodeOptions = async (): Promise<PhoneCodeProtocol | null> => {
  const http = (await import("../../core/http")).default
  return http.get<{ mark_current: number }, PhoneCodeProtocol["data"]>("/usercenter/en/cellphone_country_code_list", {
    showOriginResponse: true,
    params: {
      mark_current: 1,
    },
  }) as Promise<PhoneCodeProtocol | null>
}

const usePhoneCodeOptions = (mode: AccountInputMode) => {
  const [state, setState] = React.useState<PhoneCodeState>(() => getInitialPhoneCodeState(mode))
  const mountedRef = React.useRef(false)
  const generationRef = React.useRef(0)
  const loadingRef = React.useRef(false)
  const loadedRef = React.useRef(state.options.length > 0)
  const modeRef = React.useRef(mode)
  modeRef.current = mode

  const ensureLoaded = React.useCallback(async () => {
    if (!canLoadPhoneCodes(modeRef.current) || loadedRef.current || loadingRef.current) return
    const generation = generationRef.current
    loadingRef.current = true
    setState((current) => ({ ...current, loading: true, failed: false }))

    try {
      const options = await loadPhoneCodeOptions({
        request: requestPhoneCodeOptions,
        storage: getPhoneCodeOptionsStorage(typeof window === "undefined" ? undefined : window),
      })
      if (mountedRef.current && generationRef.current === generation && canLoadPhoneCodes(modeRef.current)) {
        loadedRef.current = true
        setState({ options, loading: false, failed: false })
      }
    } catch {
      if (mountedRef.current && generationRef.current === generation && canLoadPhoneCodes(modeRef.current)) {
        setState((current) => ({ ...current, loading: false, failed: true }))
      }
    } finally {
      loadingRef.current = false
    }
  }, [])

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      generationRef.current += 1
    }
  }, [])

  React.useEffect(() => {
    generationRef.current += 1
    loadingRef.current = false
    if (mode === "email") {
      setState((current) => (current.loading ? { ...current, loading: false } : current))
      return
    }
    if (loadedRef.current) return

    return initializePhoneCodeOptions({
      storage: getPhoneCodeOptionsStorage(window),
      onCacheHit: (options) => {
        loadedRef.current = true
        setState({ options, loading: false, failed: false })
      },
      run: () => void ensureLoaded(),
      scheduler: {
        requestIdleCallback: window.requestIdleCallback?.bind(window),
        cancelIdleCallback: window.cancelIdleCallback?.bind(window),
        setTimeout: window.setTimeout.bind(window),
        clearTimeout: window.clearTimeout.bind(window),
      },
    })
  }, [ensureLoaded, mode])

  return { ...state, ensureLoaded }
}

const filterPhoneCodeOption = (input: string, option: PhoneCodeOption): boolean =>
  `${option.label} ${option.searchText ?? ""}`.toLowerCase().includes(input.trim().toLowerCase())

export const AccountInput = ({
  mode,
  locale,
  value,
  onChange,
  disabled = false,
  error,
  onBlur,
  onEnter,
  className,
  id,
  name,
  placeholder,
}: AccountInputProps) => {
  const t = React.useMemo(() => createTranslator(normalizeLocale(locale)), [locale])
  const text = React.useMemo(
    () => ({
      emailPlaceholder: t("accountInput.emailPlaceholder"),
      phonePlaceholder: t("accountInput.phonePlaceholder"),
      phoneCodePlaceholder: t("accountInput.phoneCodePlaceholder"),
      emailAriaLabel: t("accountInput.emailPlaceholder"),
      phoneAriaLabel: t("accountInput.phonePlaceholder"),
      loadFailed: t("accountInput.loadFailed"),
      retry: t("accountInput.retry"),
      noResults: t("accountInput.noResults"),
    }),
    [t]
  )
  const generatedId = React.useId()
  const inputId = id ?? `${generatedId}-account`
  const listboxId = `${generatedId}-phone-options`
  const errorId = `${generatedId}-error`
  const phoneCodeRef = React.useRef<HTMLSpanElement>(null)
  const autoFillHandledOptionsRef = React.useRef<PhoneCodeOption[] | null>(null)
  const hasEnteredAccountRef = React.useRef(Boolean(value.account))
  const [validationVisible, setValidationVisible] = React.useState(false)
  const [selectOpen, setSelectOpen] = React.useState(false)
  const [search, setSearch] = React.useState<string | null>(null)
  const [activeIndex, setActiveIndex] = React.useState(0)
  const type = resolveAccountType(value.account, mode)
  const isPhone = type === "phone"
  const phoneCodes = usePhoneCodeOptions(mode)
  const selectedOption = phoneCodes.options.find((option) => option.value === value.phoneCode)
  const selectedPhoneCodeLabel = selectedOption?.label ?? value.phoneCode
  const searchText = search ?? ""
  const filteredOptions = React.useMemo(
    () => phoneCodes.options.filter((option) => filterPhoneCodeOption(searchText, option)),
    [phoneCodes.options, searchText]
  )
  const validationError = getAccountValidationError(value, mode)
  const internalError = validationVisible && validationError ? t(`signInCommon.${validationError}`) : ""
  const displayedError = error || internalError
  const hasVisibleError = Boolean(displayedError)

  const emitChange = React.useCallback(
    (nextValue: AccountInputValue) => {
      onChange(nextValue, getAccountHasError(nextValue, mode))
    },
    [mode, onChange]
  )

  React.useEffect(() => {
    if (autoFillHandledOptionsRef.current === phoneCodes.options) return
    autoFillHandledOptionsRef.current = phoneCodes.options
    if (value.phoneCode) return
    const currentOption = phoneCodes.options.find((option) => option.isCurrent)
    if (currentOption) emitChange({ ...value, phoneCode: currentOption.value })
  }, [emitChange, phoneCodes.options, value])

  React.useEffect(() => {
    if (!selectOpen) return
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!isEventWithinElement(event, phoneCodeRef.current)) {
        setSelectOpen(false)
        setSearch(null)
      }
    }
    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("touchstart", handlePointerDown, { passive: true })
    return () => {
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("touchstart", handlePointerDown)
    }
  }, [selectOpen])

  React.useEffect(() => {
    if (!isPhone && selectOpen) {
      setSelectOpen(false)
      setSearch(null)
    }
  }, [isPhone, selectOpen])

  const openSelect = () => {
    if (disabled) return
    void phoneCodes.ensureLoaded()
    if (selectOpen) return
    setActiveIndex(0)
    setSearch(null)
    setSelectOpen(true)
  }

  const selectOption = (option: PhoneCodeOption) => {
    emitChange({ ...value, phoneCode: option.value })
    setSelectOpen(false)
    setSearch(null)
  }

  const handleSelectKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape" || event.key === "Tab") {
      setSelectOpen(false)
      setSearch(null)
      return
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return
    event.preventDefault()
    if (!selectOpen) {
      openSelect()
      return
    }
    if (!filteredOptions.length) return
    if (event.key === "Enter") {
      const option = filteredOptions[Math.min(activeIndex, filteredOptions.length - 1)]
      if (option) selectOption(option)
      return
    }
    setActiveIndex((current) => {
      const delta = event.key === "ArrowDown" ? 1 : -1
      return (current + delta + filteredOptions.length) % filteredOptions.length
    })
  }

  const handleAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value
    if (rawValue) {
      hasEnteredAccountRef.current = true
    } else if (hasEnteredAccountRef.current) {
      setValidationVisible(true)
    }
    const nextType = resolveAccountType(rawValue, mode)
    const nextValue = { ...value, account: nextType === "email" ? rawValue.trim() : rawValue }
    if (nextType === "phone") void phoneCodes.ensureLoaded()
    emitChange(nextValue)
  }

  return (
    <span className="smm-account-field">
      <span
        className={[
          "smm-account-input",
          isPhone ? "smm-account-input-phone" : "smm-account-input-email",
          hasVisibleError && "smm-account-input-error",
          disabled && "smm-account-input-disabled",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
        part="account-input"
        data-account-type={type}
      >
        <span ref={phoneCodeRef} className="smm-account-phone-code" part="phone-code">
          <input
            className="smm-account-main-input smm-account-phone-code-input"
            type="text"
            value={selectOpen ? (search ?? "") : selectedPhoneCodeLabel}
            placeholder={selectedPhoneCodeLabel || text.phoneCodePlaceholder}
            disabled={disabled || !isPhone}
            role="combobox"
            aria-expanded={selectOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              selectOpen && filteredOptions[activeIndex] ? `${listboxId}-${activeIndex}` : undefined
            }
            tabIndex={isPhone ? 0 : -1}
            onFocus={openSelect}
            onClick={openSelect}
            onChange={(event) => {
              if (!selectOpen) openSelect()
              setSearch(event.target.value)
              setActiveIndex(0)
            }}
            onKeyDown={handleSelectKeyDown}
          />
          <span className="smm-account-phone-code-arrow" aria-hidden="true">
            ▾
          </span>
          {selectOpen ? (
            <span
              id={listboxId}
              className="smm-account-phone-options"
              role="listbox"
              aria-busy={phoneCodes.loading || undefined}
            >
              {phoneCodes.loading ? (
                <span className="smm-account-phone-loading" aria-hidden="true">
                  <span className="smm-account-phone-spinner" />
                </span>
              ) : phoneCodes.failed ? (
                <span className="smm-account-phone-status smm-account-phone-status-error" role="alert">
                  <span>{text.loadFailed}</span>
                  <button type="button" onClick={() => void phoneCodes.ensureLoaded()}>
                    {text.retry}
                  </button>
                </span>
              ) : filteredOptions.length ? (
                filteredOptions.map((option, index) => (
                  <button
                    id={`${listboxId}-${index}`}
                    key={option.value}
                    className={["smm-account-phone-option", index === activeIndex && "smm-account-phone-option-active"]
                      .filter(Boolean)
                      .join(" ")}
                    type="button"
                    role="option"
                    aria-selected={option.value === value.phoneCode}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => selectOption(option)}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <span className="smm-account-phone-status" role="status">
                  {text.noResults}
                </span>
              )}
            </span>
          ) : null}
        </span>

        <span className="smm-account-main" part="account-field">
          <input
            id={inputId}
            name={name}
            className="smm-account-main-input"
            type="text"
            autoComplete="username"
            value={value.account}
            placeholder={placeholder ?? (isPhone ? text.phonePlaceholder : text.emailPlaceholder)}
            aria-label={isPhone ? text.phoneAriaLabel : text.emailAriaLabel}
            disabled={disabled}
            aria-invalid={hasVisibleError || undefined}
            aria-describedby={hasVisibleError ? errorId : undefined}
            onChange={handleAccountChange}
            onBlur={(event) => {
              if (hasEnteredAccountRef.current) setValidationVisible(true)
              onBlur?.(event)
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                event.preventDefault()
                setValidationVisible(true)
                onEnter?.()
              }
            }}
          />
          {value.account ? (
            <button
              type="button"
              aria-label="Clear input"
              className="smm-account-input-action"
              disabled={disabled}
              onClick={() => {
                setValidationVisible(true)
                emitChange({ ...value, account: "" })
              }}
            >
              <ClearIcon />
            </button>
          ) : null}
        </span>
      </span>
      {hasVisibleError ? (
        <div id={errorId} className="smm-auth-field-error-message" role="alert">
          {displayedError}
        </div>
      ) : (
        <div className="smm-auth-field-error-placeholder"></div>
      )}
    </span>
  )
}
